import clsx from 'clsx';
import Icon from 'components/atoms/Icon';
import {
  ProjectGetAllowedTransitionsDocument,
  ProjectPageGetProjectByIdDocument,
  ProjectStatus,
  ProjectValidatePolicyActionsDocument,
  SzApp,
  useProjectGetAllowedTransitionsQuery,
  useProjectPageGetProjectByIdQuery,
  useProjectUpdateStatusMutation,
} from 'generated/graphql';
import { DEFAULT_ACTIONS } from 'hooks/useProjectPolicyActions/useProjectPolicyActions.helpers';
import { useMemo, useState } from 'react';
import ProjectStatusChangeModal from 'views/Project/components/ProjectStatusDropdown/ProjectStatusChangeModal';
import { ALL_STATUS_DROPDOWN_OPTIONS } from 'views/Project/components/ProjectStatusDropdown/ProjectStatusDropdown.constants';
import {
  DropdownMenu,
  Loader,
  TNotification,
  useNotification,
} from '@spotted-zebra-uk/ui-components';
import styles from './ProjectStatusDropdown.module.scss';

const ERROR_MESSAGES_FOR_PROJECTS_WITH_CANDIDATES = {
  [ProjectStatus.Draft]: {
    title: 'Unable to change role status to Draft',
    message:
      'This role has candidates and cannot be unpublished by moving to Draft.',
  },
  [ProjectStatus.Review]: {
    title: "Unable to change role status to 'In review'",
    message:
      "This role has candidates and cannot be unpublished by moving to 'In review'.",
  },
};

const ProjectStatusDropdown = ({
  projectId,
  projectStatus,
  projectName,
  disabled,
}: {
  projectId: number;
  projectStatus: ProjectStatus;
  projectName: string;
  disabled: boolean;
}) => {
  const { handleMsgType } = useNotification();
  const [isStatusChangeModalOpen, setIsStatusChangeModalOpen] = useState(false);
  const [selectedNewStatus, setSelectedNewStatus] =
    useState<ProjectStatus | null>(null);
  const { data, loading } = useProjectGetAllowedTransitionsQuery({
    variables: { projectId, app: SzApp.AdminApp },
    onError(error) {
      handleMsgType({
        type: TNotification.error,
        title: 'Unable to get allowed role statuses',
        message: error?.message || 'Something went wrong',
        dismissable: true,
      });
    },
  });

  const { data: projectData } = useProjectPageGetProjectByIdQuery({
    variables: { id: projectId },
  });

  const projectStatusLabel =
    // Default to Draft if no status is found
    ALL_STATUS_DROPDOWN_OPTIONS.find(
      (option) => option.status === projectStatus,
    )?.label || ALL_STATUS_DROPDOWN_OPTIONS[1].label;

  const filteredOptions = useMemo(
    () =>
      ALL_STATUS_DROPDOWN_OPTIONS.filter((option) =>
        data?.ProjectGetAllowedTransitions.includes(option.status),
      ),
    [data?.ProjectGetAllowedTransitions],
  );

  const [updateStatus, { loading: updateStatusLoading }] =
    useProjectUpdateStatusMutation({
      refetchQueries: [
        {
          query: ProjectGetAllowedTransitionsDocument,
          variables: { projectId, app: SzApp.AdminApp },
        },
        {
          //  Every time the status is updated, we need to recheck the validity of the project policy actions
          query: ProjectValidatePolicyActionsDocument,
          variables: {
            projectId,
            actions: DEFAULT_ACTIONS,
            app: SzApp.AdminApp,
          },
        },
        {
          query: ProjectPageGetProjectByIdDocument,
          variables: { id: projectId },
        },
      ],
      onCompleted: () => {
        handleCloseStatusChangeModal();
      },
      onError(error) {
        handleMsgType({
          type: TNotification.error,
          title: 'Unable to update role status',
          message: error?.message || 'Something went wrong',
          dismissable: true,
        });
      },
    });

  const handleOpenStatusChangeModal = (newStatus: ProjectStatus) => {
    const hasAnyStage = Boolean(
      projectData?.project?.assessmentStagesV2?.length ||
        projectData?.project?.interviewStages?.length ||
        projectData?.project?.shortlistStages?.length,
    );

    // We will block the user from moving a project to Draft or "In review" if it has candidates
    if (
      (newStatus === ProjectStatus.Draft ||
        newStatus === ProjectStatus.Review) &&
      Boolean(projectData?.project?.projectCandidatesCount)
    ) {
      handleMsgType({
        type: TNotification.error,
        title: ERROR_MESSAGES_FOR_PROJECTS_WITH_CANDIDATES[newStatus].title,
        message: ERROR_MESSAGES_FOR_PROJECTS_WITH_CANDIDATES[newStatus].message,
        dismissable: true,
      });
      return;
    }

    // We will block the user from moving a project to Published if it has no stages
    if (newStatus === ProjectStatus.Published && !hasAnyStage) {
      handleMsgType({
        type: TNotification.error,
        title: 'Unable to publish role',
        message:
          'A role must have at least 1 stage before it can be transitioned to Published.',
        dismissable: true,
      });
      return;
    }
    setSelectedNewStatus(newStatus);
    setIsStatusChangeModalOpen(true);
  };

  const handleCloseStatusChangeModal = () => {
    setIsStatusChangeModalOpen(false);
    setSelectedNewStatus(null);
  };

  const handleChangeStatus = (status: ProjectStatus) => {
    updateStatus({
      variables: { projectId, status },
    });
  };

  if (loading) {
    return (
      <div className={styles.loaderContainer}>
        <Loader />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <DropdownMenu
        label={projectStatusLabel}
        size="large"
        showIcon={false}
        disabled={updateStatusLoading || disabled}
        rightIcon={
          disabled ? undefined : (
            <Icon icon="chevron" className={styles.chevronIcon} />
          )
        }
        className={clsx(
          styles[projectStatus.toLowerCase()],
          styles.buttonContainer,
          disabled && styles.disabledButtonContainer,
        )}
        classNameDropdownMenu={styles.dropdownMenuContainer}
        options={filteredOptions.map((option) => ({
          ...option,
          onClick: () => handleOpenStatusChangeModal(option.status),
        }))}
      />
      {isStatusChangeModalOpen && selectedNewStatus && (
        <ProjectStatusChangeModal
          projectName={projectName}
          currentStatus={projectStatus}
          newStatus={selectedNewStatus}
          onClose={handleCloseStatusChangeModal}
          onSubmit={() => handleChangeStatus(selectedNewStatus)}
          loading={updateStatusLoading}
        />
      )}
    </div>
  );
};

export default ProjectStatusDropdown;
